import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Tooltip } from '@mantine/core';
import { FORMAT_TEXT_COMMAND, LexicalEditor, TextFormatType } from 'lexical';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import React from 'react';
import { IconType } from 'react-icons';
import { MdCode, MdFormatBold, MdFormatItalic, MdFormatStrikethrough, MdFormatUnderlined, MdLink } from 'react-icons/md';
import { mapEntries } from 'shared/collection';
import { KbdComboTip, KbdCombo } from 'components/Keyboard';
type FormatMeta = {
  icon: IconType;
  title: string;
  shortcut?: KbdCombo;
  action?: (props: {
    editor: LexicalEditor;
    selected: boolean;
  }) => void;
};
const formatMeta = {
  bold: {
    icon: MdFormatBold,
    title: 'Bold',
    shortcut: {
      mods: ['Meta'],
      key: 'B'
    }
  },
  italic: {
    icon: MdFormatItalic,
    title: 'Italic',
    shortcut: {
      mods: ['Meta'],
      key: 'I'
    }
  },
  underline: {
    icon: MdFormatUnderlined,
    title: 'Underline',
    shortcut: {
      mods: ['Meta'],
      key: 'U'
    }
  },
  code: {
    icon: MdCode,
    title: 'Code'
  },
  strikethrough: {
    icon: MdFormatStrikethrough,
    title: 'Strike-though'
  },
  link: {
    icon: MdLink,
    title: 'Link',
    action: ({
      editor,
      selected
    }) => editor.dispatchCommand(TOGGLE_LINK_COMMAND, selected ? null : 'https://')
  } as FormatMeta
} satisfies Record<string, FormatMeta>;
const standardFormatTypes = ['bold', 'underline', 'strikethrough', 'italic', 'code', 'subscript', 'superscript'] as const;
export type FormatType = keyof typeof formatMeta;
export const isStandardFormatType = (s: string): s is TextFormatType => standardFormatTypes.includes(s as any);
export const formatTypes = mapEntries(formatMeta as Record<FormatType, FormatMeta>, (type, v) => ({
  ...v,
  type
}));
export const formatTypesStandard = formatTypes.map(t => t.type as string).filter(isStandardFormatType);
export const ToolbarFormatButton = t0 => {
  const $ = _c(18);
  const {
    type,
    selected: t1,
    editor,
    size: t2,
    action,
    icon,
    title,
    shortcut
  } = t0;
  const selected = t1 === undefined ? false : t1;
  const size = t2 === undefined ? 20 : t2;
  let t3;
  if ($[0] !== shortcut || $[1] !== title) {
    t3 = <KbdComboTip title={title} combo={shortcut} />;
    $[0] = shortcut;
    $[1] = title;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== action || $[4] !== editor || $[5] !== selected || $[6] !== type) {
    t4 = () => {
      if (action) {
        action?.({
          editor,
          selected
        });
      } else {
        if (isStandardFormatType(type)) {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, type);
        }
      }
    };
    $[3] = action;
    $[4] = editor;
    $[5] = selected;
    $[6] = type;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = selected ? "primary" : "gray";
  let t6;
  if ($[8] !== icon || $[9] !== size) {
    t6 = icon({
      size
    });
    $[8] = icon;
    $[9] = size;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <ActionIcon onClick={t4} color={t5}>{t6}</ActionIcon>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t7) {
    t8 = <Tooltip label={t3} position="bottom">{t7}</Tooltip>;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};